/**
 * How the karaoke reaches the player from wherever they happen to be standing.
 *
 * Pure, and deliberately not part of `WebAudioManager`: the interesting part of
 * a sound that lives in a room is the arithmetic, not the node graph, and the
 * arithmetic is worth testing without a browser. The mixer's only job is to
 * follow what this returns - see `karaoke.test.ts` for the shape of the walk
 * from the pirtti to the juhlasali.
 *
 * Presentation only. Nothing here is reachable from `src/sim`.
 */

import { KARAOKE } from '../config/audio';
import { clamp } from '../utils/math';

export interface ListenerMix {
  /** 0 - 1, multiplied into the cue's usual gain. */
  gain: number;
  /** Lowpass corner in Hz: low is a wall in the way, high is the same room. */
  cutoff: number;
}

/**
 * Inside the navetta's four outer walls.
 *
 * A box rather than a room-by-room test on purpose: the eteinen, the WCs and
 * the juhlasali are all *in the building with the music*, and the only edge
 * anybody can hear is the one at the front door.
 */
export function insideVenue(x: number, z: number): boolean {
  const { venue } = KARAOKE;
  return x >= venue.minX && x <= venue.maxX && z >= venue.minZ && z <= venue.maxZ;
}

/** Distance from the rig, in metres, ignoring height. */
export function karaokeDistance(x: number, z: number): number {
  return Math.hypot(x - KARAOKE.source.x, z - KARAOKE.source.z);
}

/**
 * The mix for anything that stands at `source` and makes a noise, heard from
 * (x, z). The karaoke rig is one such thing; the projector's clip is another.
 *
 * One function rather than two because the room is the same room: how far the
 * sound has come and whether the navetta's wall is in the way is a property of
 * the building, not of what is playing. Only the *place* differs, so only the
 * place is a parameter.
 */
export function placedMix(x: number, z: number, source: { x: number; z: number }): ListenerMix {
  const distance = Math.hypot(x - source.x, z - source.z);
  const reach = clamp((KARAOKE.silent - distance) / (KARAOKE.silent - KARAOKE.full), 0, 1);
  // Squared rather than linear. Sound does not thin out evenly with distance,
  // and a straight ramp reads as somebody walking beside you with their hand on
  // the volume knob - most of the growth belongs in the last stretch.
  //
  // That ramp is tuned across forty-five metres of yard, though, and inside a
  // room those numbers are far too flat: it leaves the singing at seven tenths
  // fourteen metres down the hall, which is the far end of the room, where the
  // screen is meant to be the loud thing rather than the rig behind you. So the
  // near field is the inverse-distance law instead - half the level each time
  // the distance doubles from `full` - and whichever of the two is quieter
  // wins. They meet at `full` and cross back out in the yard, so the seam is
  // continuous: the room belongs to the inverse law, and the last stretch to
  // silence belongs to the ramp.
  const carry = Math.min(reach * reach, KARAOKE.full / Math.max(KARAOKE.full, distance));
  const wall = insideVenue(x, z) ? KARAOKE.inside : KARAOKE.outside;
  return { gain: carry * wall.gain, cutoff: wall.cutoff };
}

export function karaokeMix(x: number, z: number): ListenerMix {
  return placedMix(x, z, KARAOKE.source);
}

/** What the mixer plays before the game has said where anybody is standing. */
export const SILENT_MIX: ListenerMix = { gain: 0, cutoff: KARAOKE.outside.cutoff };
